import {
  dispatchAnalyticsEventWithAction,
  updateWindowDataLayer,
  buildErrorAnalyticsData,
  buildPageLoadAnalyticsData,
  updateAppointmentsDetailsInWindowDataLayer,
  formatAppointmentEditsInWindowDataLayer,
  getAppointmentEdits,
} from './analytics.utils';

export const triggerPageLoadAnalytics = (path, shopId, appointmentsDetails, customerInfo) => {
  const data = buildPageLoadAnalyticsData(path, shopId, appointmentsDetails, customerInfo);
  if (data) {
    dispatchAnalyticsEventWithAction('pageload');
    updateWindowDataLayer(data);
  }
};

export const triggerErrorAnalytics = (path, shopId, errorMessage) => {
  const data = buildErrorAnalyticsData(path, shopId, errorMessage);
  if (data) {
    dispatchAnalyticsEventWithAction('error');
    updateWindowDataLayer(data);
  }
};

export const triggerApiErrorAnalytics = (shopId, errorMessage) => {
  triggerErrorAnalytics('apiError', shopId, errorMessage);
};

export const triggerSearchShopAnalytics = () => {
  dispatchAnalyticsEventWithAction('appointment.shopfinderClicked');
};

export const triggerSelectTimeSlotAnalytics = () => {
  dispatchAnalyticsEventWithAction('appointment.dateClicked');
};

export const triggerSearchShopByLocationAnalytics = () => {
  dispatchAnalyticsEventWithAction('appointment.useLocationClicked');
};

export const triggerClickStylistDropdownAnalytics = () => {
  dispatchAnalyticsEventWithAction('appointment.stylistDropdownClicked');
};

export const triggerClickAddToCalendarAnalytics = () => {
  dispatchAnalyticsEventWithAction('appointment.addToCalendarClicked');
};

export const triggerClickAddEditAppointmentAnalytics = ({ category, type }) => {
  dispatchAnalyticsEventWithAction('reviewEditDetails', { category, type });
};

export const triggerSignMeUpAnalytics = () => {
  const data = buildPageLoadAnalyticsData('sign-up.modal');
  dispatchAnalyticsEventWithAction('rtb.bannerSignup');
  updateWindowDataLayer(data);
};

export const triggerSubmitCancellingReason = (reason) => {
  dispatchAnalyticsEventWithAction('account.appointmentCancelled', { Reason: reason });
};

export const triggerSelectFittingTypeAnalytics = (fittingTypes) => {
  dispatchAnalyticsEventWithAction('appointments.shoeType');
  updateAppointmentsDetailsInWindowDataLayer({ shoeFit: Object.values(fittingTypes).join('|') });
};

export const triggerSelectNumberOfChildrenAnalytics = (appointmentDetails) => {
  dispatchAnalyticsEventWithAction('appointments.shoeType');
  updateAppointmentsDetailsInWindowDataLayer(appointmentDetails);
};

export const triggerEditPageClickedAnalytics = (editSectionType) => {
  dispatchAnalyticsEventWithAction('appointment.reviewPageEditClick', { type: editSectionType });
  const appointmentEdits = getAppointmentEdits();
  updateAppointmentsDetailsInWindowDataLayer({ appointmentEdits: appointmentEdits || '' });
};

export const triggerEditPageChangesMadeAnalytics = (editSectionType, appointmentEdits) => {
  dispatchAnalyticsEventWithAction('appointment.reviewPageEdited', { type: editSectionType });
  const formattedAppointmentEdits = formatAppointmentEditsInWindowDataLayer(appointmentEdits);
  updateAppointmentsDetailsInWindowDataLayer({ appointmentEdits: formattedAppointmentEdits });
};

export const triggerEditPageSaveEditsAnalytics = (type) => {
  dispatchAnalyticsEventWithAction('appointment.reviewPageEditsSaved', { type });
};

export const triggerFilterAppliedAnalytics = (filterApplied) => {
  dispatchAnalyticsEventWithAction('appointment.filterApplied', filterApplied);
};

export const triggerFilterClearedAnalytics = () => {
  dispatchAnalyticsEventWithAction('appointment.filterCleared');
};

export const triggerGoToBrandWebsiteAnalytics = (brand) => {
  dispatchAnalyticsEventWithAction('appointment.goToBrandWebsite', { brand });
};

export const triggerCallBrandPhoneNumberAnalytics = (brand) => {
  dispatchAnalyticsEventWithAction('appointment.callBrandPhoneNumber', { brand });
};

export const triggerPartnerGroupChangeAnalytics = (stylistLevel) => {
  dispatchAnalyticsEventWithAction('appointment.stylistUpdated');
  updateAppointmentsDetailsInWindowDataLayer({ stylistLevel });
};

export const triggerAvailabilityInteractionAnalytics = (type, extraAppointmentsDetails) => {
  dispatchAnalyticsEventWithAction('appointment.availabilityInteraction', { type });
  if (extraAppointmentsDetails) {
    updateAppointmentsDetailsInWindowDataLayer(extraAppointmentsDetails);
  }
};

export const triggerTreatmentModalOpened = (treatment) => {
  dispatchAnalyticsEventWithAction('appointment.modal', { type: 'open' });
  updateAppointmentsDetailsInWindowDataLayer({
    ...window.jlData.page.appointments,
    modal: {
      type: 'treatment',
      category: treatment.category.name,
      brand: treatment.brand.name,
    },
  });
};

export const triggerPreferredAppointmentTypeChosen = (stringWithType) => {
  const [type] = stringWithType && stringWithType.trim().split(' ');
  dispatchAnalyticsEventWithAction('appointments.appointmentType', { type });
  updateAppointmentsDetailsInWindowDataLayer({
    ...window.jlData.page.appointments,
    preferredAppointmentType: type,
  });
};

export const triggerViewMoreInfoAnalytics = (brand) => {
  dispatchAnalyticsEventWithAction('appointments.viewMoreInfo', { brand });
};

export const triggerAddProductAnalytics = (product) => {
  const jlDataProducts = window.jlData?.page?.appointments?.products || [];

  if (!jlDataProducts.some((jlDataProduct) => jlDataProduct === product.name)) {
    dispatchAnalyticsEventWithAction('appointment.productsUpdated');
    updateAppointmentsDetailsInWindowDataLayer({
      products: [...jlDataProducts, product.name],
    });
  }
};

export const triggerRemoveProductAnalytics = (removedProduct, products) => {
  const jlDataProducts = window.jlData?.page?.appointments?.products || [];

  if (!products.some((product) => product.name === removedProduct.name)) {
    updateAppointmentsDetailsInWindowDataLayer({
      products: jlDataProducts.filter((jlDataProduct) => jlDataProduct !== removedProduct.name),
    });
  }
};

export const analyticsListeners = {
  triggerPartnerGroupChangeAnalytics,
  triggerEditPageChangesMadeAnalytics,
  triggerSelectTimeSlotAnalytics,
  triggerAvailabilityInteractionAnalytics,
  triggerCallBrandPhoneNumberAnalytics,
  triggerViewMoreInfoAnalytics,
  triggerAddProductAnalytics,
  triggerRemoveProductAnalytics,
};
